import axios from "axios";
import { DateTime } from "luxon";
import process from "prop-types/prop-types";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";

import Breadcrumbs from "../../component/breadcrumbs";
import CustomHelmet from "../../component/CustomHelmet";
import Form from "../../component/Form";
import FormInput from "../../component/FormInput";
import FormMultiSelect from "../../component/FormMultiSelect";
import FormSelect from "../../component/FormSelect";
import FullScreenLoader from "../../component/FullScreenLoader";
import Intro from "../../component/Intro";
import BaseContainer from "../../component/layout/base-container";
import { getToken } from "../../lib/authentication";
import { custom_fetcher } from "../../lib/fetcher";
import { request_methods } from "../../lib/request_methods";
import { subjectsList } from "../../lib/subjectsList";
import { SCHOOL_STEP, SCHOOL_TYPE } from "../../lib/swiss_schools";

export default function NewSurvey({ user }) {
  const navigate = useNavigate();

  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(DateTime.now().toISODate());
  const [schoolType, setSchoolType] = useState("");
  const [schoolStep, setSchoolStep] = useState([]);

  const [noTeachers, setNoTeachers] = useState(1);
  const [noExperts, setNoExperts] = useState(1);
  const [noStudents, setNoStudents] = useState(29);

  const { data } = useSWR(
    [`${process.env.REACT_APP_BASE_URL}surveyruns/`, request_methods.options],
    custom_fetcher
  );
  if (!data) return <FullScreenLoader />;

  return (
    <>
      <CustomHelmet pageTitle="Neue Umfrage" />

      <Breadcrumbs
        listItems={[
          { name: "Home", url: "/" },
          { name: "Umfragen", url: "/dashboard" },
          { name: "Neue Umfrage", url: "/dashboard/neue-umfrage" },
        ]}
      />
      <BaseContainer>
        <Intro title={"Neue Umfrage"} />
        <div className="TextImage">
          <div className="TextImage--inner">
            <div className="TextImage--content">
              <Form>
                <p>
                  Für die Erstellung einer neuen Umfrage ist es wichtig, zu
                  wissen, von wie vielen Lehrpersonen, Schüler*innen und
                  Beobachter*innen Rückmeldungen zum Unterricht eingeholt werden
                  sollen.
                </p>

                <p>
                  Falls Sie nicht alleine unterrichten und Sie den Unterricht
                  mit weiteren Lehrpersonen evaluieren möchten, geben Sie
                  einfach die entsprechende Anzahl an beteiligten Lehrpersonen
                  ein. Falls Sie alleine unterrichten, können Sie 1 angeben.
                </p>

                <p>
                  Dasselbe Prinzip gilt für die Angaben zur Anzahl der
                  Beobachter*innen und Schüler*innen. Sollten Sie eine der
                  beiden Perspektiven nicht brauchen, geben Sie 0 an. Bei den
                  Schüler*innen können Sie zunächst eine erwartete Anzahl der
                  Schüler*innen angeben. Anpassungen sind im Nachhinein möglich.
                </p>
                <FormInput
                  label="Wie viele Umfragen sollen für Lehrpersonen erstellt werden?"
                  required
                  value={noTeachers}
                  onChange={(e) => {
                    setNoTeachers(parseInt(e.target.value) || null);
                  }}
                />
                <FormInput
                  label="Wie viele Umfragen sollen für Beobachter*innen erstellt werden?"
                  required
                  value={noExperts}
                  onChange={(e) => {
                    setNoExperts(parseInt(e.target.value) || null);
                  }}
                />
                <FormInput
                  label="Wie viele Umfragen sollen für Schüler*innen erstellt werden?"
                  required
                  value={noStudents}
                  onChange={(e) => {
                    setNoStudents(parseInt(e.target.value) || null);
                  }}
                />
                <FormInput
                  label={
                    "Bitte beschreiben Sie die Klasse, in der Sie eine Umfrage durchführen wollen. Die Beschreibung hilft Ihnen, die Klasse wieder zuordnen zu können (Beispiel: 6a)."
                  }
                  required
                  onChange={(e) => setDescription(e.target.value)}
                />
                <FormInput
                  label="Bitte geben Sie das Datum der Umfrage an."
                  required
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
                <FormSelect
                  label="Bitte wählen Sie die Schulart aus."
                  required
                  options={SCHOOL_TYPE.map((type) => ({
                    value: type,
                    text: type,
                  }))}
                  value={schoolType}
                  onChange={(e) => setSchoolType(e.target.value)}
                />
                <FormMultiSelect
                  label="Bitte wählen Sie die Klassenstufe(n) aus."
                  required
                  options={SCHOOL_STEP.map((step) => ({
                    value: step,
                    text: step,
                  }))}
                  value={schoolStep}
                  onChange={(value) => setSchoolStep(value)}
                />
                <FormSelect
                  label="Bitte wählen Sie das Fach aus."
                  required
                  options={subjectsList.map((subject) => ({
                    value: subject,
                    text: subject,
                  }))}
                  onChange={(e) => setSubject(e.target.value)}
                />

                <div className="FormButtons">
                  <button
                    className="Button color-primary size-large"
                    style={{
                      marginRight: "unset",
                      marginLeft: "auto",
                    }}
                    onClick={async (e) => {
                      e.preventDefault();
                      await axios.post(
                        `${process.env.REACT_APP_BASE_URL}surveyruns/`,
                        {
                          subject: subject,
                          date: date,
                          description: description,
                          school_type: schoolType,
                          school_step: schoolStep,
                          no_teacher_surveys: noTeachers,
                          no_expert_surveys: noExperts,
                          no_student_surveys: noStudents,
                        },
                        {
                          headers: { Authorization: `Token ${getToken()}` },
                        }
                      );
                      navigate("/dashboard");
                    }}
                  >
                    Erstellen
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </BaseContainer>
    </>
  );
}
